
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PricingHeader from "./pricing/components/PricingHeader";
import StrategyCard from "./pricing/components/StrategyCard";
import ComparisonTable from "./pricing/components/ComparisonTable";
import PricingToggle from "./pricing/components/PricingToggle";
import ActionButtons from "./pricing/components/ActionButtons";
import ChatOption from "@/components/ChatOption";
import { useIsMobile } from "@/hooks/use-mobile";

const FreePro = () => {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);
  const monthlyPrice = 29;
  const yearlyPrice = 290;
  const monthlySavings = monthlyPrice * 12 - yearlyPrice;
  const savingsPercentage = Math.round(monthlySavings / (monthlyPrice * 12) * 100);
    const isMobile = useIsMobile();
  
  const handleSubscribe = () => {
    navigate('/payment', {
      state: {
        isYearly,
        amount: isYearly ? yearlyPrice : monthlyPrice
      }
    });
  };
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-corporate-primary-25 to-corporate-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative px-4 py-24 bg-gradient-to-r from-corporate-primary-100 to-corporate-primary-75 text-white">
        <div className="absolute inset-0 bg-[url('/lovable-uploads/8fd12166-0122-42a4-9da3-b430e2991c7a.png')] bg-center bg-no-repeat opacity-10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center mb-4"
          >
            <div className="h-1 w-20 bg-corporate-secondary-100 mr-4"></div>
            <span className="text-corporate-secondary-75 font-semibold">METRADE PRICING</span>
            <div className="h-1 w-20 bg-corporate-secondary-100 ml-4"></div>
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-bold mb-6 leading-tight"
          >
            Choose The Right Plan For Your Trading
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl opacity-90 max-w-2xl mx-auto"
          >
            Unlock verified strategies and premium features to enhance your trading journey
          </motion.p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      <main className="max-w-[1400px] mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <PricingHeader />
        </motion.div>

        <div className="max-w-[1200px] mx-auto mb-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-corporate-primary-100 mb-6">
              Metrade Strategies: Free vs Premium
            </h2>
            <p className="text-corporate-neutral-75 text-base sm:text-lg max-w-3xl mx-auto">
              Whether you're just starting or looking to level up, Metrade offers two tiers of strategies to fit your needs. Free Strategies let you explore community-sourced ideas at no cost, while Metrade Premium unlocks expert-vetted strategies, advanced analytics, and premium support for a professional trading experience.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <StrategyCard type="free" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <StrategyCard type="premium" />
            </motion.div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-lg text-corporate-primary-100 font-medium">
              Ready to upgrade? Unlock the full potential of Metrade with Premium strategies – gain confidence from expert guidance, dive into rich analytics, and enjoy dedicated support to enhance your trading journey.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <PricingToggle isYearly={isYearly} setIsYearly={setIsYearly} monthlyPrice={monthlyPrice} yearlyPrice={yearlyPrice} savingsPercentage={savingsPercentage} />
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-corporate-primary-100 mb-6">Compare why Metrade Premium is a Great Value</h2>
            <p className="text-corporate-neutral-75 text-base sm:text-lg max-w-3xl mx-auto">
              Whether you're looking for no-frills access to community-driven insights or want fully vetted strategies with premium support, our side-by-side comparison below will help you decide. See how Free stacks up against Metrade Premium—and pick the plan that matches your trading ambitions.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <ComparisonTable />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 mb-8"
        >
          <ActionButtons handleSubscribe={handleSubscribe} isYearly={isYearly} />
        </motion.div>
      </main>
      
      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="px-4 py-16 mb-16"
      >
        <div className="text-center bg-gradient-to-r from-corporate-primary-100 to-corporate-primary-75 rounded-2xl p-8 md:p-12 max-w-4xl mx-auto shadow-lg relative overflow-hidden">
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-corporate-secondary-100 rounded-full filter blur-3xl opacity-20 -mb-20 -ml-20"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-corporate-secondary-75 rounded-full filter blur-3xl opacity-20 -mt-32 -mr-32"></div>
          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl font-bold text-corporate-white mb-4">
              Ready to Elevate Your Trading Strategy?
            </h2>
            <p className="text-corporate-white/90 mb-8 text-lg max-w-2xl mx-auto">
              Join thousands of successful traders who have already discovered the advantage of METRADE's premium strategies. Start your journey today.
            </p>
            <button 
              onClick={handleSubscribe}
              className="bg-corporate-secondary-100 hover:bg-corporate-secondary-75 text-corporate-primary-100 px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Get Started Now
            </button>
          </div>
        </div>
      </motion.section>
      {/* Only show ChatOption on desktop */}
            {!isMobile && (
              <ChatOption />
            )}
      
      <Footer />
    </div>
  );
};

export default FreePro;
